import React, { useEffect, useState } from "react";
import {
  AssignTaskApi,
  GetOrganizerListApi,
  DeleteOrganizerApi,
} from "../../api/request/organizers/organizers.js";
import MySegmented from "../../components/segmented/Segmented.jsx";
import SkeletonTable from "../../components/table/SkeletonTable";
import columns from "../../columns/organizers/organizers.js";
import moment from "moment/moment";
import { GetTeamMemberListApi } from "../../api/request/team/team.js";
import { Select } from "antd";
import Spinner from "../../components/loaders/Spinner.jsx";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/modals/ConfirmationModal.jsx";
import Delete from "../../assets/Delete.jsx";

export default function Organizers() {
  const [segmentedValue, setSegmentedValue] = useState("Verify Doc");
  const [isLoading, setIsLoading] = useState(false);
  const [organizerList, setOrganizerList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [isOrganizerDelete, setIsOrganizerDelete] = useState(false);
  const [organizerId, setOrganizerId] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);


  const getFilterStatus = (type) => {
    switch (type) {
      case "Verify Doc":
        return "task1";
      case "Prepare Tax":
        return "task2";
      case "Review Tax":
        return "task3";
      case "Follow Up":
        return "task4";
      case "E-file":
        return "task5";
      default:
        return "task1";
    }
  };

  const getEmployeeData = (list) => {
    const activeList = (list && list.length > 0) ? list : (employeeList || []);
    const employeeData = activeList
      .filter((item) => item?.permissions?.includes(segmentedValue))
      .map((item) => {
        return { label: item?.name, value: item?.id };
      });
    return employeeData;
  };

  const getOrganizerList = async (list) => {
    try {
      setIsLoading(true);
      let params = { task_order: getFilterStatus(segmentedValue) };
      const { data } = await GetOrganizerListApi(params);
      const activeList = (list && list.length > 0) ? list : employeeList;
      const employeeData = getEmployeeData(activeList);
      if (data?.status) {
        setOrganizerList(
          data?.data?.map((item) => {
            const assignTo = employeeData?.find(
              (emp) => emp?.value === item?.employee_id
            );
            return {
              ...item,
              name: item?.user?.name,
              email: item?.user?.email,
              organizer: (
                <div className="font-bold highlight-text capitalize">
                  {item?.organizer_name_slug}
                </div>
              ),
              submit: item?.is_submit ? "Yes" : "No",
              assignTo: (
                <Select
                  placeholder="Select employee"
                  style={{ width: "100%" }}
                  value={assignTo ? assignTo.value : (item?.employee_name || item?.employee_id)}
                  options={employeeData}
                  onChange={(value) =>
                    assignHandler(item?.id, value, item?.employee_id)
                  }
                />
              ),
              startDate: item?.submitted_date
                ? moment(item?.submitted_date).format("lll")
                : "",
              due: (
                <p
                  className={`${
                    item?.is_due ? "text-red-600" : "text-green-500"
                  }`}
                >
                  {item?.is_due ? "Yes" : "No"}
                </p>
              ),
              dueDate: item?.due_date
                ? moment(item?.due_date).format("lll")
                : "",
              status: (
                <p
                  className={`${item?.status === "active" ? "text-green-500" : "text-red-600"
                    } font-bold uppercase`}
                >
                  {item?.status}
                </p>
              ),
              action: (
                <button onClick={() => { 
                  setIsOrganizerDelete(true); 
                  setOrganizerId(item?.id) 
                }}>
                  <Delete />
                </button>
              ),
            };
          })
        );
      }
      setIsLoading(false);
    } catch (error) {
      setOrganizerList([]);
      setIsLoading(false);
    }
  };

  const getEmployeeList = async () => {
    try {
      const { data } = await GetTeamMemberListApi();
      if (data?.status && data?.data) {
        setEmployeeList(data?.data);
        columns.forEach((item) => {
          if (item.dataIndex === "assignTo") {
            item.filters = data?.data?.map((emp) => {
              return { text: emp?.name, value: emp?.id };
            });
          }
        });
        return data?.data;
      }
    } catch (error) {
      console.error("Error loading employee list:", error);
    }
    return [];
  };

  useEffect(() => {
    const init = async () => {
      const empList = await getEmployeeList();
      getOrganizerList(empList);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getOrganizerList(employeeList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentedValue]);

  const setAssignData = (id, employeeId) => {
    const employeeData = getEmployeeData(employeeList);
    const empObj = (employeeList || []).find((emp) => emp?.id === employeeId);
    setOrganizerList((prev) => {
      return prev.map((item) =>
        item.id === id
          ? {
            ...item,
            employee_id: employeeId,
            employee_name: empObj?.name || item?.employee_name,
            assignTo: (
              <Select
                placeholder="Select employee"
                style={{ width: "100%" }}
                value={employeeId}
                options={employeeData}
                onChange={(value) =>
                  assignHandler(item?.id, value, employeeId)
                }
              />
            ),
          }
          : item
      );
    });
  };

  const assignHandler = async (id, employeeId, currentEmployeeId) => {
    if (employeeId === currentEmployeeId) return;
    try {
      setOrganizerList((prev) => {
        return prev.map((item) =>
          item.id === id ? { ...item, assignTo: <Spinner /> } : item
        );
      });

      let params = { organizer_id: id, employee_id: employeeId };
      const { data } = await AssignTaskApi(params);
      if (data?.status) {
        toast.success(data?.message || "Task assigned successfully");
        setAssignData(id, employeeId);
      } else {
        setAssignData(id, currentEmployeeId);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
      setAssignData(id, currentEmployeeId);
    }
  };

  const handleDeleteOrganizer = async () => {
    try {
      setIsDeleteLoading(true);
      const { data } = await DeleteOrganizerApi({ organizer_id: organizerId });
      if (data?.status) {
        toast.success(data?.message);
        getOrganizerList();
        setIsOrganizerDelete(false);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally { 
      setIsDeleteLoading(false);
    }
  }

  return (
    <div>
      <MySegmented
        options={[
          "Verify Doc",
          "Prepare Tax",
          "Review Tax",
          "Follow Up",
          "E-file",
        ]}
        value={segmentedValue}
        setValue={setSegmentedValue}
      />

      <div className="border-2 rounded-xl my-5">
        <div className="flex justify-between mx-5 mt-5">
          <p className=" font-semibold text-2xl">Organizers</p>
        </div>
        <div className="my-6">
          <SkeletonTable
            key={segmentedValue}
            searchingFields={["name", "email"]}
            dateFilterFields = {["startDate", "dueDate"]}
            columns={columns}
            data={organizerList}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Delete Organizer */}
      <ConfirmationModal
        isOpen={isOrganizerDelete}
        setIsOpen={setIsOrganizerDelete}
        message='Are you sure, want to delete this organizer?'
        onConfirm={() => handleDeleteOrganizer()}
        isLoading={isDeleteLoading}
        loadingLabel='Deleting Organizer...'
      />
    </div>
  );
}
